<template>
  <div class="task-links">
    <div class="task-links__header">
      <h3 class="task-links__title">Linked Tasks ({{ totalLinks }})</h3>
      <themed-button size="sm" @click="showLinkForm = !showLinkForm" v-if="canEdit">
        <theme-icon icon="plus-icon" type="icons" classes="w-3 h-3" />
        <span>Link Task</span>
      </themed-button>
    </div>

    <!-- Link Form -->
    <div v-if="showLinkForm" class="task-links__form">
      <div class="task-links__form-row">
        <themed-select
          v-model="newLink.link_type"
          :options="linkTypeOptions"
          placeholder="Link type"
          class="task-links__form-type"
        />
        <themed-select
          v-model="newLink.linked_task_id"
          :options="availableTaskOptions"
          placeholder="Search tasks..."
          class="task-links__form-task"
        />
        <themed-button size="sm" @click="createLink" :disabled="!newLink.linked_task_id || submitting">
          <span v-if="submitting">Linking...</span>
          <span v-else>Add</span>
        </themed-button>
        <themed-button variant="secondary" size="sm" @click="cancelLinkForm">Cancel</themed-button>
      </div>
      <span v-if="linkError" class="task-links__error">{{ linkError }}</span>
    </div>

    <!-- Outgoing Links -->
    <div v-if="outgoingLinks.length" class="task-links__section">
      <div
        v-for="link in outgoingLinks"
        :key="'out-' + link.id"
        class="task-links__item"
      >
        <span class="task-links__type-badge">{{ formatLinkType(link.link_type) }}</span>
        <span class="task-links__task-title">
          <a 
            v-if="link.linked_task && link.linked_task.task_key" 
            :href="`/tasks/${link.linked_task.task_key}`" 
            target="_blank"
            class="task-links__task-number"
            @click.stop
          >
            {{ link.linked_task.task_key }}
          </a>
          {{ link.linked_task ? link.linked_task.title : 'Unknown' }}
        </span>
        <span v-if="link.linked_task && link.linked_task.status" class="task-links__status-badge" :style="{ backgroundColor: getStatusColor(link.linked_task.status) }">
          {{ link.linked_task.status.name }}
        </span>
        <themed-button
          v-if="canEdit"
          variant="ghost"
          size="sm"
          @click="removeLink(link)"
          aria-label="Remove link"
        >
          <theme-icon icon="trash" type="icons" classes="w-3 h-3" />
        </themed-button>
      </div>
    </div>

    <!-- Incoming Links -->
    <div v-if="incomingLinks.length" class="task-links__section">
      <div
        v-for="link in incomingLinks"
        :key="'in-' + link.id"
        class="task-links__item"
      >
        <span class="task-links__type-badge">{{ formatInverseLinkType(link.link_type) }}</span>
        <span class="task-links__task-title">
          <a 
            v-if="link.task && link.task.task_key" 
            :href="`/tasks/${link.task.task_key}`" 
            target="_blank"
            class="task-links__task-number"
            @click.stop
          >
            {{ link.task.task_key }}
          </a>
          {{ link.task ? link.task.title : 'Unknown' }}
        </span>
        <span v-if="link.task && link.task.status" class="task-links__status-badge" :style="{ backgroundColor: getStatusColor(link.task.status) }">
          {{ link.task.status.name }}
        </span>
        <themed-button
          v-if="canEdit"
          variant="ghost"
          size="sm"
          @click="removeLink(link)"
          aria-label="Remove link"
        >
          <theme-icon icon="trash" type="icons" classes="w-3 h-3" />
        </themed-button>
      </div>
    </div>

    <div v-if="!outgoingLinks.length && !incomingLinks.length && !showLinkForm" class="task-links__empty">
      No linked tasks.
    </div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'TaskLinks',

  components: {
    ThemedButton,
    ThemedSelect,
    ThemeIcon
  },

  props: {
    taskId: {
      type: [Number, String],
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      outgoingLinks: [],
      incomingLinks: [],
      availableTasks: [],
      showLinkForm: false,
      submitting: false,
      linkError: '',
      newLink: {
        linked_task_id: null,
        link_type: 'related'
      }
    };
  },

  computed: {
    totalLinks() {
      return this.outgoingLinks.length + this.incomingLinks.length;
    },

    linkTypeOptions() {
      return [
        { label: 'Related to', value: 'related' },
        { label: 'Blocks', value: 'blocks' },
        { label: 'Blocked by', value: 'blocked_by' },
        { label: 'Duplicates', value: 'duplicates' },
        { label: 'Duplicated by', value: 'duplicated_by' }
      ];
    },

    availableTaskOptions() {
      // Exclude current task and already-linked tasks
      const linkedIds = [
        ...this.outgoingLinks.map(l => l.linked_task_id),
        ...this.incomingLinks.map(l => l.task_id),
        parseInt(this.taskId)
      ];
      return this.availableTasks
        .filter(t => !linkedIds.includes(t.id))
        .map(t => ({ label: t.title, value: t.id }));
    }
  },

  watch: {
    taskId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchLinks();
          this.fetchAvailableTasks();
        }
      }
    }
  },

  methods: {
    async fetchLinks() {
      try {
        const response = await axios.get(`/api/tasks/${this.taskId}/links`);
        this.outgoingLinks = response.data.data.outgoing || [];
        this.incomingLinks = response.data.data.incoming || [];
      } catch (error) {
        console.error('Error fetching task links:', error);
      }
    },

    async fetchAvailableTasks() {
      try {
        const response = await axios.get('/api/tasks', { params: { per_page: 200 } });
        this.availableTasks = response.data.data || response.data;
      } catch (error) {
        console.error('Error fetching available tasks:', error);
      }
    },

    async createLink() {
      if (!this.newLink.linked_task_id) return;
      this.submitting = true;
      this.linkError = '';

      try {
        const response = await axios.post(`/api/tasks/${this.taskId}/links`, this.newLink);
        this.outgoingLinks.push(response.data);
        this.resetLinkForm();
        this.$emit('link-added', response.data);
      } catch (error) {
        this.linkError = error.response?.data?.message || 'Failed to create link.';
      } finally {
        this.submitting = false;
      }
    },

    async removeLink(link) {
      if (!confirm('Remove this task link?')) return;

      try {
        await axios.delete(`/api/tasks/${this.taskId}/links/${link.id}`);
        this.outgoingLinks = this.outgoingLinks.filter(l => l.id !== link.id);
        this.incomingLinks = this.incomingLinks.filter(l => l.id !== link.id);
        this.$emit('link-removed', link.id);
      } catch (error) {
        console.error('Error removing link:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to remove link.'
        });
      }
    },

    cancelLinkForm() {
      this.showLinkForm = false;
      this.resetLinkForm();
    },

    resetLinkForm() {
      this.newLink = { linked_task_id: null, link_type: 'related' };
      this.linkError = '';
      this.showLinkForm = false;
    },

    formatLinkType(type) {
      const labels = {
        related: 'Related to',
        blocks: 'Blocks',
        blocked_by: 'Blocked by',
        duplicates: 'Duplicates',
        duplicated_by: 'Duplicated by'
      };
      return labels[type] || type;
    },

    formatInverseLinkType(type) {
      const inverse = {
        blocks: 'Blocked by',
        blocked_by: 'Blocks',
        duplicates: 'Duplicated by',
        duplicated_by: 'Duplicates',
        related: 'Related to'
      };
      return inverse[type] || type;
    },

    getStatusColor(status) {
      if (!status || !status.color) return '#6b7280';
      const colorMap = {
        blue: '#3b82f6',
        yellow: '#eab308',
        green: '#22c55e',
        red: '#ef4444',
        orange: '#f97316',
        gray: '#6b7280'
      };
      return colorMap[status.color] || status.color;
    }
  }
};
</script>
